import {
  METRIC_FULL_FORMAT,
  fmtMoneyCompact,
  separationSankeyFlows,
  type MetricKey,
  type SankeyNode,
  type StageBridge,
} from '@integrate/core';

/*
  The population flow As-is → Day 1 → Day 2 & steady state — the summary's picture, at last.

  `SummaryPane` recorded the decision not to draw this: "a diagram that has to be right about
  proportions is worse than absent while it is approximate." The answer is not an approximation — it
  is the frozen app's OWN geometry, ported intact from `separationSankeyHtml`
  (orgchart.html:7886-7936): the same fixed three columns, the same shared scale K taken as the
  minimum across columns so every ribbon in the picture is proportional to every other, the same
  cumulative in/out offsets stacking ribbons inside their nodes, the same cubic ribbon path. The
  flows themselves come from `separationSankeyFlows`, which derives every ribbon from a stage-bridge
  result — so the picture conserves BY CONSTRUCTION: the ribbons into a node sum to the node because
  the bridge identities say so, not because the drawing was tuned.

  Ribbons take their TARGET's colour — where somebody lands is the story — except build-up inflows,
  which stay pink: they are entering the population, and painting them the target's colour would make
  hires read as transfers. Both rules are the frozen app's.

  RemainCo hires are not in this population flow, exactly as in the frozen app: they appear in the
  TSA economics. The caption says so rather than leaving the reader to count.
*/

const W = 980;
const H = 400;
const NW = 16;
const TOP = 34;
const BOT = 10;
const PAD = 16;
const COL_X = [8, 415, 830] as const;

const COLUMN_HEADS = ['AS-IS', 'AT CLOSE / DAY 1', 'DAY 2 & STEADY STATE'] as const;

export function PopulationSankey({
  bridge,
  metric,
}: {
  bridge: StageBridge;
  metric: MetricKey;
}): JSX.Element {
  const { nodes, links } = separationSankeyFlows(bridge);
  const format = METRIC_FULL_FORMAT[metric];
  // Node labels sit beside the boxes; compact money is what fits, exactly as the frozen app chose.
  const short = metric === 'cost' ? fmtMoneyCompact : format;

  if (!links.length) {
    return (
      <p className="muted" data-testid="population-sankey-empty">
        No population yet.
      </p>
    );
  }

  /*
    ONE scale for the whole picture — the minimum over the three columns — so a ribbon's width means
    the same thing wherever it lands. Per-column scales (the entity Sankey's choice) would fill the
    frame better and quietly break cross-column comparison, which on THIS chart is the content.
  */
  const usable = H - TOP - BOT;
  const cols = [0, 1, 2].map((c) => nodes.filter((n) => n.col === c));
  const K = Math.min(
    ...cols.map((cn) => {
      const sum = cn.reduce((a, n) => a + n.value, 0);
      return sum > 0 ? (usable - PAD * Math.max(0, cn.length - 1)) / sum : Infinity;
    }),
  );

  const pos = new Map<string, { node: SankeyNode; x: number; y: number; h: number }>();
  for (const cn of cols) {
    let y = TOP;
    for (const n of cn) {
      const h = Math.max(n.value * K, 1.5);
      pos.set(n.id, { node: n, x: COL_X[n.col]!, y, h });
      y += h + PAD;
    }
  }

  // Cumulative offsets, in link order: each ribbon stacks below the previous one at both ends.
  const outUsed = new Map<string, number>();
  const inUsed = new Map<string, number>();
  const ribbons = links.map((l) => {
    const s = pos.get(l.s)!;
    const t = pos.get(l.t)!;
    const w = Math.max(l.v * K, 1);
    const sy = s.y + (outUsed.get(l.s) ?? 0);
    const ty = t.y + (inUsed.get(l.t) ?? 0);
    outUsed.set(l.s, (outUsed.get(l.s) ?? 0) + w);
    inUsed.set(l.t, (inUsed.get(l.t) ?? 0) + w);
    const x0 = s.x + NW;
    const x1 = t.x;
    const mx = (x0 + x1) / 2;
    const d = [
      `M ${x0} ${sy.toFixed(1)}`,
      `C ${mx} ${sy.toFixed(1)}, ${mx} ${ty.toFixed(1)}, ${x1} ${ty.toFixed(1)}`,
      `L ${x1} ${(ty + w).toFixed(1)}`,
      `C ${mx} ${(ty + w).toFixed(1)}, ${mx} ${(sy + w).toFixed(1)}, ${x0} ${(sy + w).toFixed(1)}`,
      'Z',
    ].join(' ');
    const color = l.s === 'bu' ? 'var(--c-pink)' : t.node.color;
    return { l, d, color };
  });

  return (
    <figure className="ent-sankey" data-testid="population-sankey" data-metric={metric}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        role="img"
        aria-label="Population flow from As-is through Day 1 to Day 2 and steady state"
      >
        {COLUMN_HEADS.map((head, c) => (
          <text
            key={head}
            x={c === 0 ? COL_X[0] : c === 1 ? COL_X[1]! + NW / 2 : COL_X[2]! + NW}
            y={16}
            textAnchor={c === 0 ? 'start' : c === 1 ? 'middle' : 'end'}
            fontSize={11}
            fontWeight={700}
            fill="var(--c-muted)"
            letterSpacing="0.4"
          >
            {head}
          </text>
        ))}

        {ribbons.map(({ l, d, color }, i) => (
          <path
            key={`${l.s}→${l.t}-${i}`}
            d={d}
            fill={color}
            opacity={0.34}
            data-source={l.s}
            data-target={l.t}
            data-value={l.v}
          >
            <title>{`${l.label}: ${format(l.v)}`}</title>
          </path>
        ))}

        {[...pos.values()].map(({ node, x, y, h }) => (
          <g key={node.id} data-testid={`sankey-node-${node.id}`} data-value={node.value}>
            <rect x={x} y={y.toFixed(1)} width={NW} height={h.toFixed(1)} rx={3} fill={node.color}>
              <title>{`${node.label}: ${format(node.value)}`}</title>
            </rect>
            <text
              x={node.col === 2 ? x - 8 : x + NW + 8}
              y={y + h / 2}
              dominantBaseline="middle"
              textAnchor={node.col === 2 ? 'end' : 'start'}
              fontSize={12}
              fill="var(--c-ink)"
            >
              <tspan fontWeight={600}>{node.label}</tspan>
              <tspan fill="var(--c-muted)"> · {short(node.value)}</tspan>
            </text>
          </g>
        ))}
      </svg>

      <figcaption className="muted small">
        Ribbon width ∝ {metric === 'hc' ? 'headcount' : metric === 'fte' ? 'FTE' : 'raw salary'};
        hover any ribbon for the exact figure. Flows conserve by construction (the bridge
        identities). RemainCo hires are not part of this population flow — they appear in the TSA
        economics.
      </figcaption>

      <details className="chart-table">
        <summary>Population flows as a table</summary>
        <table className="data-table">
          <thead>
            <tr>
              <th>Flow</th>
              <th>From</th>
              <th>To</th>
              <th className="num">{metric === 'hc' ? 'Headcount' : metric === 'fte' ? 'FTE' : 'Cost'}</th>
            </tr>
          </thead>
          <tbody>
            {links.map((l, i) => (
              <tr key={i}>
                <td>{l.label}</td>
                <td>{pos.get(l.s)!.node.label}</td>
                <td>{pos.get(l.t)!.node.label}</td>
                <td className="num">{format(l.v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
